import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { User, Subscription, Plan } from '../types';
import { authService } from '../services/authService';
import onboardingService from '../services/onboardingService';

interface AuthContextType {
  user: User | null;
  subscription: Subscription | null;
  plan: Plan | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  needsOnboarding: boolean;
  login: (email: string, password: string) => Promise<{ needsOnboarding: boolean }>;
  register: (data: RegisterData) => Promise<RegisterResult>;
  logout: () => void;
  updateUser: (data: Partial<User>) => Promise<void>;
  refreshUser: () => Promise<void>;
  setNeedsOnboarding: (value: boolean) => void;
}

interface RegisterData {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone: string;
  company_name?: string;
  language?: 'pt' | 'en';
  referral_code?: string;
  plan_id?: string;
  billing_cycle?: 'monthly' | 'yearly';
}

interface RegisterResult {
  checkoutUrl?: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  const checkOnboardingStatus = useCallback(async () => {
    try {
      const status = await onboardingService.getStatus();
      setNeedsOnboarding(!status.isCompleted);
      return !status.isCompleted;
    } catch (error) {
      // If error checking onboarding, assume not needed
      setNeedsOnboarding(false);
      return false;
    }
  }, []);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await authService.getProfile();
      if (response.success && response.data) {
        setUser(response.data);
        const activeSubscription = response.data.subscriptions?.find(
          s => ['trialing', 'active'].includes(s.status)
        );
        setSubscription(activeSubscription || null);
        setPlan(activeSubscription?.plan || null);

        // Check onboarding status
        await checkOnboardingStatus();
      }
    } catch (error: any) {
      // Only clear token if it's actually invalid (401).
      // Don't clear on network errors or 500s - those are transient.
      if (error?.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, [checkOnboardingStatus]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (email: string, password: string) => {
    const response = await authService.login({ email, password });
    if (response.success && response.data) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setUser(response.data.user);

      // Load full user profile (subscription, etc.) - don't let failures block login
      try {
        await loadUser();
      } catch (err) {
        // loadUser already handles its own errors. If profile fails,
        // we still have the basic user data from login response.
        console.warn('loadUser failed after login, continuing with basic user data');
      }

      // Check onboarding (loadUser already calls this, but ensure we have the result)
      const requiresOnboarding = needsOnboarding;
      return { needsOnboarding: requiresOnboarding };
    } else {
      throw new Error(response.message);
    }
  };

  const register = async (data: RegisterData): Promise<RegisterResult> => {
    const response = await authService.register(data);
    if (response.success && response.data) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setUser(response.data.user);

      // New users always need onboarding
      setNeedsOnboarding(true);

      await loadUser();

      // Return checkoutUrl if provided (for paid plans)
      return { checkoutUrl: response.data.checkoutUrl };
    } else {
      throw new Error(response.message);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('currentProjectId');
    setUser(null);
    setSubscription(null);
    setPlan(null);
  };

  const updateUser = async (data: Partial<User>) => {
    const response = await authService.updateProfile(data);
    if (response.success && response.data) {
      setUser(response.data);
    } else {
      throw new Error(response.message);
    }
  };

  const refreshUser = async () => {
    await loadUser();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        subscription,
        plan,
        isLoading,
        isAuthenticated: !!user,
        needsOnboarding,
        login,
        register,
        logout,
        updateUser,
        refreshUser,
        setNeedsOnboarding
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
